/**
 * Printable stock BIN label sheet (garment-stock bin-labels plan v1 PR-2).
 *
 * One label per stocked shelf identity (per size), at the chosen printer's physical size — the same
 * two formats, the same `#label-print` portal isolation, the same `@page`-only-while-printing
 * mechanism as the hem-tag sheet (`label-sheet.tsx`), whose stylesheet this reuses verbatim (the bin
 * rules live at the tail of `LABEL_CSS`; a second stylesheet copy would drift from the paper-only
 * findings recorded there).
 *
 * Unlike `LabelSheet` this takes its rows as props — the browse page already holds the on-hand
 * views, and the post-intake offers hold the row the write just returned — so there is no query and
 * no block-reason gate: a bin label is pure identity, reprintable freely, with nothing rank-shaped
 * to go stale (`docs/label-mislabel-mitigation-plan-v1.md` § 8.4 does not apply here).
 *
 * ⚠️ Two inherited invariants, both paper-only defects if broken:
 *  - the `@page` effect keeps `format` in its dep list (a stale closure prints the previous
 *    geometry while the preview looks right — the label-sheet lesson at its :395);
 *  - nothing renders after the last `.label` inside `.label-list` (`.label:last-of-type` matches on
 *    tag name; a trailing node feeds a blank label through the printer).
 */
import { Check, Printer, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { type BinLabel, binLabelsForRows } from './bin-label-lib';
import type { GarmentStockRow } from './garment-stock-lib';
import {
  LABEL_FORMATS,
  type LabelFormat,
  labelFormatVars,
  labelPageRule,
  loadLabelFormat,
  saveLabelFormatId,
} from './label-format';
import { LABEL_CSS } from './label-sheet';

export function BinLabelCard({ label }: { label: BinLabel }) {
  return (
    <div className="label bin">
      <div className="qr">
        {/* level M + a 4-module quiet zone, exactly like the hem tag. The payload is 15 chars ⇒ QR v2
            (25 modules) — at the same 17/20 mm boxes that's ~0.7–0.8 mm/module, well ABOVE the
            0.486 mm/module pitch the hardware spike validated for the far denser garment codes. */}
        <QRCodeSVG value={label.code} size={64} level="M" marginSize={4} />
        {/* The dominant style code (back-first), else the binId — see BinLabel.qrCaption. The extra
            .bin-under class caps it at the QR's width (a 12-char code would otherwise widen the
            column and shove the text block); the hex always rides IN the QR regardless. */}
        <div className="order-under bin-under">{label.qrCaption}</div>
      </div>
      <div className="txt">
        {/* Layout option 1 (owner pick, 2026-09-07): size box leads, the DOMINANT design name bold
            at 3.8mm below it (two lines allowed), the other placements small on one "+ …" line, the
            blank last. No BACK/FRONT words — the big name is always the back/custom design. */}
        <div className="bin-hdr">
          {/* Verbatim size, 7 mm boxed — a `?` for a nameless size is meant to alarm (divider rule). */}
          <span className="ssize">{label.sizeName || '?'}</span>
          {label.kind && <span className="bin-kind">{label.kind}</span>}
        </div>
        <div className={label.primary.isCode ? 'bin-name mono' : 'bin-name'}>
          {label.primary.text}
        </div>
        {label.othersLine && <div className="bin-plus">{label.othersLine}</div>}
        <div className="blank">{label.blankLabel}</div>
      </div>
    </div>
  );
}

export function BinLabelSheet({
  rows,
  styleCodes,
  title,
  onClose,
}: {
  rows: readonly GarmentStockRow[];
  /** designId → styleCode, the browse page's existing live join. */
  styleCodes: ReadonlyMap<string, string>;
  title: string;
  onClose: () => void;
}) {
  const [format, setFormat] = useState<LabelFormat>(() => loadLabelFormat());
  const chooseFormat = (f: LabelFormat) => {
    setFormat(f);
    saveLabelFormatId(f.id);
  };

  // Escape closes the overlay (matches the pick-list / PO / label-sheet portals).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The chosen format's @page, injected ONLY across a print. `format` MUST stay in the dep list —
  // with `[]` the listener closes over the mount-time format and the preview updates while the PAGE
  // stays stale (Chrome then scales to fit: a plausible, scannable label at the wrong size).
  useEffect(() => {
    const el = document.createElement('style');
    el.textContent = labelPageRule(format);
    const before = () => {
      if (!el.isConnected) document.head.appendChild(el);
    };
    const after = () => el.remove();
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
      el.remove();
    };
  }, [format]);

  const { labels, skipped } = binLabelsForRows(rows, styleCodes);

  return createPortal(
    <div
      id="label-print"
      role="dialog"
      aria-modal="true"
      aria-label="Print bin labels"
      className="fixed inset-0 z-50 overflow-auto bg-background p-4 sm:p-6 print:static print:overflow-visible print:bg-transparent print:p-0"
    >
      <style>
        {labelFormatVars(format)}
        {LABEL_CSS}
      </style>
      <div className="mx-auto mb-4 flex max-w-3xl items-start justify-between gap-2 print:hidden">
        <div>
          <div className="font-semibold">Print bin labels — {title}</div>
          <div className="text-sm text-muted-foreground">
            {labels.length} label{labels.length === 1 ? '' : 's'}, one per size
            {skipped > 0 &&
              ` · ${skipped} row${skipped === 1 ? '' : 's'} skipped (no bin id yet — reload the page)`}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Bag each size's stack and stick its label on the bag — the QR answers “what is this
            stack?” at the Scan station.
            <br />
            Laid out for <span className="font-medium text-foreground">{format.name}</span> — pick
            that same printer in the print dialog, with margins <em>None</em> and scale{' '}
            <em>100%</em>, never “Fit”.
          </div>
        </div>
        <div className="flex flex-none flex-col items-end gap-2">
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.print()}
              disabled={labels.length === 0}
            >
              <Printer className="size-4" />
              Print
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="size-4" />
              Close
            </Button>
          </div>
          {/* Selection via `secondary` + tick + aria-pressed, never `default` — this theme's
              --primary and --destructive are the same red (the label-sheet note). */}
          <fieldset className="m-0 flex flex-wrap justify-end gap-1 border-0 p-0">
            <legend className="sr-only">Label format</legend>
            {LABEL_FORMATS.map((f) => {
              const active = f.id === format.id;
              return (
                <Button
                  key={f.id}
                  size="sm"
                  variant={active ? 'secondary' : 'outline'}
                  aria-pressed={active}
                  onClick={() => chooseFormat(f)}
                >
                  {active && <Check className="size-3.5" />}
                  {f.name}
                </Button>
              );
            })}
          </fieldset>
        </div>
      </div>

      {labels.length === 0 && (
        <p className="mx-auto max-w-3xl text-sm text-muted-foreground print:hidden">
          Nothing to print{skipped > 0 ? ' — reload the page and try again.' : '.'}
        </p>
      )}

      <div className="label-list">
        {labels.map((l) => (
          <BinLabelCard key={l.code} label={l} />
        ))}
      </div>
    </div>,
    document.body,
  );
}
